import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { BellRing, Coffee, MessageCircle, ShieldCheck } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useSite } from "@/context/SiteContext";
import { scrollToId } from "@/utils";
import { LogoMark } from "@/components/Logo";
import { DEFAULT_IMAGES } from "@/config";

export const Hero = () => {
  const { t, lang } = useLang();
  const { heroText, img, waLink } = useSite();
  const h = heroText(lang);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const badgeY = useTransform(scrollYProgress, [0, 1], [0, 40]);

  const line = (node, i) => (
    <span className="reveal-line">
      <motion.span
        className="block"
        initial={{ y: "112%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1, delay: 0.15 + i * 0.16, ease: [0.16, 1, 0.3, 1] }}
      >
        {node}
      </motion.span>
    </span>
  );

  return (
    <section ref={ref} className="relative overflow-hidden pb-16 pt-28 sm:pb-24 sm:pt-36">
      <div className="pointer-events-none absolute -end-40 -top-40 h-[540px] w-[540px] rounded-full bg-sun-light/80 blur-3xl" />
      <div className="pointer-events-none absolute -start-52 top-1/3 h-[460px] w-[460px] rounded-full bg-lilac/30 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-48 start-1/3 h-[420px] w-[420px] rounded-full bg-blush/40 blur-3xl" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <motion.span
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-line bg-white/80 px-4 py-1.5 text-xs font-bold tracking-wide text-inksoft"
          >
            <span className="h-2 w-2 rounded-full bg-terra" />
            {t.hero.badge}
          </motion.span>

          <h1 className="mt-6 font-display text-[clamp(3.1rem,7.5vw,6.2rem)] font-extrabold leading-[0.98] tracking-tight text-ink">
            {line(h.line1, 0)}
            {line(<span className="text-pine">{h.line2}</span>, 1)}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-inksoft sm:text-lg"
          >
            {h.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <motion.button
              data-testid="hero-quote-button"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => scrollToId("quote")}
              className="rounded-full bg-pine px-7 py-4 font-bold text-cream shadow-[0_16px_32px_-16px_rgba(94,33,196,0.5)] transition-colors hover:bg-pine-dark"
            >
              {t.hero.ctaQuote}
            </motion.button>
            <motion.a
              data-testid="hero-whatsapp-order-button"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              href={waLink(lang)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2.5 rounded-full bg-terra px-7 py-4 font-bold text-white transition-colors hover:bg-terra-dark"
            >
              <MessageCircle className="h-5 w-5" />
              {t.hero.ctaWhats}
            </motion.a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-9 flex flex-wrap gap-3"
          >
            {[
              [BellRing, t.hero.b1, "text-terra"],
              [Coffee, t.hero.b2, "text-pine"],
              [ShieldCheck, t.hero.b3, "text-pine"],
            ].map(([Icon, label, color], i) => (
              <span key={i} className="flex items-center gap-2 rounded-full border border-line bg-white/80 px-4 py-2 text-sm font-semibold text-inksoft">
                <Icon className={`h-4 w-4 ${color}`} />
                {label}
              </span>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5">
          <motion.div style={{ y: imgY }} className="relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="relative overflow-hidden rounded-[2.5rem] border border-cardline shadow-[0_48px_90px_-42px_rgba(94,33,196,0.35)]"
            >
              <img src={img("img_hero", DEFAULT_IMAGES.hero)} alt={t.hero.imgAlt} className="aspect-[4/5] w-full object-cover" />
            </motion.div>

            <motion.div
              style={{ y: badgeY }}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.05 }}
              className="absolute -bottom-7 start-2 sm:start-6"
            >
              <motion.div
                animate={{ y: [0, -9, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                className="flex max-w-[290px] items-start gap-3 rounded-2xl border border-cardline bg-white/95 p-3.5 shadow-[0_24px_48px_-24px_rgba(34,24,51,0.35)] backdrop-blur"
                data-testid="hero-whatsapp-bubble"
              >
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-pine-light">
                  <LogoMark className="h-7 w-7" inverted />
                </span>
                <span>
                  <span className="block text-sm font-bold text-ink">{t.hero.floatTitle}</span>
                  <span className="block text-[13px] leading-snug text-inksoft">{t.hero.floatMsg}</span>
                  <span className="mt-0.5 block text-[11px] font-semibold text-inkmute">{t.hero.floatTime}</span>
                </span>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
